/** Работа с датами в часовом поясе семьи без внешних библиотек. */

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

/** Части локального времени в поясе tz для момента ms. */
export function zonedParts(ms: number, tz: string) {
  const out: Record<string, number> = {};
  for (const p of partsFormatter(tz).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year!,
    month: out.month!,
    day: out.day!,
    hour: out.hour!,
    minute: out.minute!,
    second: out.second!,
  };
}

/** Смещение пояса от UTC в минутах для момента ms (Москва = +180). */
export function tzOffsetMinutes(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

/** Локальная дата 'YYYY-MM-DD' момента ms в поясе tz. */
export function localDate(ms: number, tz: string): string {
  const p = zonedParts(ms, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Начало и конец (не включительно) локальных суток в UTC-миллисекундах. */
export function dayRange(date: string, tz: string): { start: number; end: number } {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const startGuess = Date.UTC(y, m - 1, d);
  const start = startGuess - tzOffsetMinutes(startGuess, tz) * 60000;
  const endGuess = Date.UTC(y, m - 1, d + 1);
  const end = endGuess - tzOffsetMinutes(endGuess, tz) * 60000;
  return { start, end };
}

/** Сдвиг даты 'YYYY-MM-DD' на n дней. */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** Минуты с начала локальных суток для момента ms. */
export function minuteOfDay(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  return p.hour * 60 + p.minute;
}

/** «1 ч 45 мин», «12 мин», «0 мин». */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} мин`;
  if (r === 0) return `${h} ч`;
  return `${h} ч ${r} мин`;
}

/** «12:34» для таймера (минуты:секунды), с часами при необходимости «1:02:05». */
export function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(r).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** «08:35» в поясе tz. */
export function formatClock(ms: number, tz: string): string {
  const p = zonedParts(ms, tz);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/** Часовой пояс завода: «сегодня» для проверок и смен. */
export const FACTORY_TZ = 'Europe/Moscow';
